import './coapplicantEditPersonalPage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import lowerCase from 'lodash/lowerCase';
import toString from 'lodash/toString';
import trim from 'lodash/trim';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { PhoneNumberInput } from '../../../components/PhoneNumberInput/PhoneNumberInput';
import {
  emailValidator,
  nameValidator,
} from '../../../components/utils/inputValidators';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useUserStore from '../../../store/userStore/useUserStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const {
    computed: { coapplicantReviewEditPathname, coapplicantReviewEditProgress },
    coapplicantFirstName,
    coapplicantLastName,
    coapplicantPrimaryEmailAddress,
    coapplicantPrimaryPhoneNumber,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantFirstName,
    setCoapplicantLastName,
    setCoapplicantPrimaryEmailAddress,
    setCoapplicantPrimaryPhoneNumber,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    computed: { userEmailAddress, userPhoneNumber },
  } = useUserStore();

  const form = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      firstNameValue: coapplicantFirstName,
      lastNameValue: coapplicantLastName,
      primaryEmailAddressValue: coapplicantPrimaryEmailAddress,
      primaryPhoneNumberValue: coapplicantPrimaryPhoneNumber || '',
    },
    delayError: 200,
    mode: 'onBlur',
    shouldFocusError: false,
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = form;

  const firstNameValidation = {
    required: t('validation.firstName.required'),
    minLength: {
      value: 2,
      message: t('validation.firstName.minLength', { min: 2 }),
    },
    pattern: {
      value: nameValidator,
      message: t('validation.firstName.invalid'),
    },
  };

  const lastNameValidation = {
    required: t('validation.lastName.required'),
    minLength: {
      value: 2,
      message: t('validation.lastName.minLength', { min: 2 }),
    },
    pattern: {
      value: nameValidator,
      message: t('validation.lastName.invalid'),
    },
  };

  const emailAddressValidation = {
    setValueAs: (value: string): string => trim(value),
    required: t('validation.emailAddress.required'),
    pattern: {
      value: emailValidator,
      message: t('validation.emailAddress.invalid'),
    },
    validate: (value: string): string | boolean =>
      lowerCase(value) !== lowerCase(userEmailAddress) ||
      t('validation.emailAddress.applicantConflict'),
  };

  const phoneNumberValidation = (value: string): string | boolean =>
    lowerCase(value) !== lowerCase(userPhoneNumber) ||
    t('validation.phone.applicantConflict');

  const finishEditing = (): void => {
    navigate(coapplicantReviewEditPathname);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const {
      firstNameValue,
      lastNameValue,
      primaryEmailAddressValue,
      primaryPhoneNumberValue,
    } = values;

    if (
      !firstNameValue ||
      !lastNameValue ||
      !primaryEmailAddressValue ||
      !primaryPhoneNumberValue
    ) {
      return;
    }

    setCoapplicantFirstName(firstNameValue);
    setCoapplicantLastName(lastNameValue);
    setCoapplicantPrimaryEmailAddress(primaryEmailAddressValue);
    setCoapplicantPrimaryPhoneNumber(primaryPhoneNumberValue);

    if (isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        finishEditing();
      }

      return;
    }

    finishEditing();
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="coapplicant-edit-personal-page" sx={theme.pageLayout}>
        <Header
          progress={coapplicantReviewEditProgress}
          showDrawerToggle
          onBackClick={finishEditing}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantEditPersonal.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantEditPersonal.form.h1}>
                {t('CoapplicantEditPersonalPage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantEditPersonal.form.h2}>
                {t('CoapplicantEditPersonalPage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.coapplicantEditPersonal.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-inputs">
                  <div className="input-half">
                    <TextField
                      {...register('firstNameValue', firstNameValidation)}
                      error={!isEmpty(errors.firstNameValue)}
                      label={t('CoapplicantEditPersonalPage.firstName')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.coapplicantEditPersonal.form
                              .firstNameInput,
                        },
                      }}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.firstNameValue) && (
                      <span
                        className="field-error"
                        data-testid={
                          locator.pages.coapplicantEditPersonal.form
                            .firstNameInputError
                        }
                      >
                        {toString(errors.firstNameValue.message)}
                      </span>
                    )}
                  </div>

                  <div className="input-half">
                    <TextField
                      {...register('lastNameValue', lastNameValidation)}
                      error={!isEmpty(errors.lastNameValue)}
                      label={t('CoapplicantEditPersonalPage.lastName')}
                      slotProps={{
                        htmlInput: {
                          'data-testid':
                            locator.pages.coapplicantEditPersonal.form
                              .lastNameInput,
                        },
                      }}
                      variant="outlined"
                      fullWidth
                    />

                    {!isEmpty(errors.lastNameValue) && (
                      <span
                        className="field-error"
                        data-testid={
                          locator.pages.coapplicantEditPersonal.form
                            .lastNameInputError
                        }
                      >
                        {toString(errors.lastNameValue.message)}
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <PhoneNumberInput
                    control={control}
                    errorTestId={
                      locator.pages.coapplicantEditPersonal.form.phoneInputError
                    }
                    inputLabelKey={'CoapplicantEditPersonalPage.phone'}
                    name="primaryPhoneNumberValue"
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.coapplicantEditPersonal.form.phoneInput,
                      },
                    }}
                    validate={phoneNumberValidation}
                  />
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <TextField
                    {...register(
                      'primaryEmailAddressValue',
                      emailAddressValidation,
                    )}
                    error={!isEmpty(errors.primaryEmailAddressValue)}
                    label={t('CoapplicantEditPersonalPage.email')}
                    slotProps={{
                      htmlInput: {
                        'data-testid':
                          locator.pages.coapplicantEditPersonal.form
                            .emailAddressInput,
                      },
                    }}
                    variant="outlined"
                    fullWidth
                  />

                  {!isEmpty(errors.primaryEmailAddressValue) && (
                    <span
                      className="field-error"
                      data-testid={
                        locator.pages.coapplicantEditPersonal.form
                          .emailAddressInputError
                      }
                    >
                      {toString(errors.primaryEmailAddressValue.message)}
                    </span>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <div className="inline-buttons">
                  <Button
                    color="secondary"
                    data-testid={
                      locator.pages.coapplicantEditPersonal.form.cancelButton
                    }
                    loadingPosition="start"
                    startIcon={<></>}
                    type="button"
                    variant="contained"
                    fullWidth
                    onClick={finishEditing}
                  >
                    {t('CoapplicantEditPersonalPage.cancel')}
                  </Button>

                  <Button
                    color="primary"
                    data-testid={
                      locator.pages.coapplicantEditPersonal.form.submitButton
                    }
                    disabled={!isValid}
                    loading={isSyncingCoapplicant}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('CoapplicantEditPersonalPage.save')}
                  </Button>
                </div>
              </div>
            </div>
            <footer
              data-testid={locator.pages.coapplicantEditPersonal.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantEditPersonalPage';
